import { createLogger } from '../../logging/index.js';
import { pullRepository } from '../../plugin/git-pull.js';
import { JobService } from '../../services/job.service.js';
import { estimateIndexingTime } from '../../utils/eta.js';
import { estimateFileCount } from '../../utils/file-count.js';
import { spawnBackgroundWorker } from '../../workers/spawn-worker.js';
import { StoresPullArgsSchema } from '../schemas/index.js';
import type { StoresPullArgs } from '../schemas/index.js';
import type { ToolHandler, ToolResponse } from '../types.js';

const logger = createLogger('mcp-pull');

/**
 * Handle stores:pull requests
 *
 * Pulls latest changes from git remote for a repo-type store,
 * then triggers a background re-index job.
 */
export const handleStoresPull: ToolHandler<StoresPullArgs> = async (
  args,
  context
): Promise<ToolResponse> => {
  const validated = StoresPullArgsSchema.parse(args);
  logger.info({ store: validated.store }, 'Stores pull started');

  const { services, options } = context;

  const store = await services.store.getByIdOrName(validated.store);

  if (store === undefined) {
    throw new Error(`Store not found: ${validated.store}`);
  }

  if (store.type !== 'repo') {
    throw new Error(
      `stores:pull only works on repo-type stores. Store "${store.name}" is type "${store.type}".`
    );
  }

  if (store.url === undefined) {
    throw new Error(`Store "${store.name}" has no git remote URL. Cannot pull without a remote.`);
  }

  // Pull latest changes from git remote
  const pullResult = await pullRepository({
    repoPath: store.path,
    branch: store.branch,
  });

  if (!pullResult.success) {
    logger.error({ store: store.name, error: pullResult.error.message }, 'Git pull failed');
    throw new Error(`Git pull failed for "${store.name}": ${pullResult.error.message}`);
  }

  const pullOutput = pullResult.data;
  logger.info({ store: store.name, pullOutput }, 'Git pull succeeded');

  // Create background job for re-indexing
  const jobService = new JobService(options.dataDir);
  const job = jobService.createJob({
    type: 'index',
    details: {
      storeName: store.name,
      storeId: store.id,
      phase: 'indexing',
      phaseStep: 1,
      phaseTotalSteps: 1,
      path: store.path,
    },
    message: `Re-indexing ${store.name} after git pull...`,
  });

  spawnBackgroundWorker(job.id, options.dataDir);

  logger.info({ storeId: store.id, storeName: store.name, jobId: job.id }, 'Stores pull completed');

  // Estimate file count and ETA for re-indexing
  const fileCount = await estimateFileCount(store.path);
  const eta = estimateIndexingTime(fileCount);
  const fileCountLabel = fileCount > 0 ? `~${String(fileCount)} files` : 'files';

  return {
    content: [
      {
        type: 'text',
        text: JSON.stringify(
          {
            store: {
              id: store.id,
              name: store.name,
            },
            pull: {
              output: pullOutput,
            },
            search: {
              immediate: `Files available for Grep/Read at: ${store.path}`,
              pending: `Similarity search re-indexing ${fileCountLabel}`,
              eta: eta.etaDisplay,
              monitor: `check job status with job:status ${job.id}`,
            },
            job: {
              id: job.id,
              status: job.status,
              message: `Re-indexing ${store.name} after git pull (${fileCountLabel}, ${eta.etaDisplay})...`,
            },
            message: `Pulled latest changes. Files available for Grep/Read at ${store.path}. Similarity search re-indexing ${fileCountLabel} (${eta.etaDisplay}).`,
          },
          null,
          2
        ),
      },
    ],
  };
};
